
import Historico from "@/components/familia/FamiliaHistorico.vue";
import Menor from "@/components/familia/Menor.vue";
import CrearMenorModal from "@/components/modals/CrearMenorModal.vue";
import MenorServicioIndividualModal from "@/components/modals/MenorServicioIndividualModal.vue";
import MenorServicioProgramadoModal from "@/components/modals/MenorServicioProgramadoModal.vue";
import SendEmailModal from "@/components/modals/SendEmailModal.vue";
import ServiciosColectivosTabla from "@/components/servicios/TablaColectivosFamilias.vue";
import ServiciosTabla from "@/components/servicios/TablaFamilias.vue";
import CurrencyInput from "@/components/widgets/CurrencyInput.vue";
import comarcas from "@/core/data/comarcas";
import { numeroMenoresOpcionesTodas } from "@/core/data/numeroMenores";
import { tipologiasFamiliaTodas } from "@/core/data/tipologiasFamilia";
import tratamientos from "@/core/data/tratamientos";
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb";
import { ServicioEstado } from "@/enums";
import Page from "@/layout/Page.vue";
import { DocumentoModificado } from "@/store/models";
import {
  AuthModule,
  EmailsModule,
  FamiliasListadoModule,
} from "@/store/modules";
import { Documento, IAMFile } from "@/types";
import { ArrowDown } from "@element-plus/icons-vue";
import imageCompression from "browser-image-compression";
import Dropzone from "dropzone";
import { ElMessage, ElNotification } from "element-plus";
import Swal from "sweetalert2/dist/sweetalert2.js";
import { ErrorMessage, useField, useForm } from "vee-validate";
import { computed, defineComponent, onBeforeMount, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import { getModule } from "vuex-module-decorators";
import * as Yup from "yup";

interface Familia {
  tratamiento: string;
  nombre: string;
  apellidos: string;
  dni: string;
  email: string;
  telefono: string;
  direccion: string;
  provincia: string;
  localidad: string;
  comarcaId: string;
  tipologiaFamilia: string;
  numeroMenores: string;
  conIngresos: boolean;
  ingresos: string;
  familiaPrioritaria: boolean;
  documentosEliminar?: string[];
  documentosNuevos?: IAMFile[];
  documentosModificados?: DocumentoModificado[];
}

interface SendEmailPayload {
  asunto: string;
  mensaje: string;
  targetType: string;
  targetId: string;
}

export default defineComponent({
  name: "formularios-familia-detalle",
  components: {
    ErrorMessage,
    ServiciosTabla,
    ServiciosColectivosTabla,
    Page,
    ArrowDown,
    Historico,
    Menor,
    CrearMenorModal,
    MenorServicioProgramadoModal,
    MenorServicioIndividualModal,
    SendEmailModal,
    CurrencyInput,
  },

  methods: {
    removeDocumento(index) {
      let documentoId = this.documentos[index].id as string;
      if (!this.documentosEliminar) {
        this.documentosEliminar = [];
      }
      this.documentosEliminar.push(documentoId);
      this.documentos.splice(index, 1);
    },

    includeDocumentoNuevo(file) {
      const reader = new FileReader();
      return new Promise<void>((resolve, reject) => {
        reader.readAsDataURL(file);
        reader.onload = () => {
          this.documentosNuevos.push({
            nombre: file.name,
            data: reader.result?.toString() || "",
          });
          if (Object.keys(this.erroresDocumentos).length === 0) {
            this.isReading = false;
          } else {
            this.isReading = true;
          }
          resolve();
        };
        reader.onerror = () => {
          this.erroresDocumentos.value[file.name] = true;
          this.isReading = true;
          reject();
        };
      });
    },
    deleteDocumentoNuevo(file) {
      this.documentosNuevos = this.documentosNuevos.filter(function (item) {
        return item.nombre !== file.name;
      });

      if (Object.keys(this.erroresDocumentos).length === 0) {
        this.isReading = false;
      }
    },
    compressImage(file) {
      const compressionOptions = { maxSizeMB: 0.3 };
      return imageCompression(file, compressionOptions);
    },
    // Inicia edición inline del nombre
    startEditNombre(index) {
      this.editingIndex = index;
      this.editingNombre = this.documentos[index]?.nombre ?? "";
    },
    // Confirma edición: actualiza documentos[] y documentosModificados (manteniendo la última edición por id)
    commitEditNombre(index, id) {
      const nuevo = (this.editingNombre || "").trim();
      // si vacío, no cambiamos nada
      if (!nuevo) {
        this.editingIndex = null;
        this.editingNombre = "";
        return;
      }
      // actualizar nombre en la lista visible
      if (this.documentos[index]) {
        this.documentos[index].nombre = nuevo;
      }

      // Asegurarnos de insertar/actualizar en documentosModificados
      // documentosModificados puede estar proxied por Vee-Validate; soportamos ambos accesos
      const dmRaw = this.documentosModificados;

      if (dmRaw) {
        const existente = dmRaw.find((d) => d.id === id);
        if (existente) {
          existente.nombre = nuevo;
        } else {
          dmRaw.push({ id: id, nombre: nuevo });
        }
      } else {
        // en caso extremo, inicializamos
        if (!this.documentosModificados) {
          this.documentosModificados = [{ id: id, nombre: nuevo }];
        }
      }

      this.editingIndex = null;
      this.editingNombre = "";
    },
  },
  setup() {
    const { t } = useI18n();
    const route = useRoute();
    const router = useRouter();
    const token = route.params.id;
    const sendEmailContext = ref({ targetType: "", targetId: "" });
    const prepareSendEmail = (): void => {
      // establecer contexto antes de que Bootstrap abra la modal
      sendEmailContext.value = {
        targetType: "FAMILIA",
        targetId: token as string,
      };
    };
    const familia = ref();
    const store = useStore();
    const module = getModule(FamiliasListadoModule, store);
    const authModule = getModule(AuthModule, store);
    const emailsModule = getModule(EmailsModule, store);
    const comarcaId = authModule.user.comarcaId;
    const error = computed(() => module.errors);
    // handler que recibe el evento emitido por el modal de enviar correo
    const onSendEmail = async (payload: SendEmailPayload): Promise<void> => {
      try {
        await emailsModule.enviar(payload);
        ElNotification({
          message: t("page.acciones.enviarCorreoExito"),
          type: "success",
        });
      } catch (err) {
        ElNotification({
          message: t("page.acciones.enviarCorreoError"),
          type: "error",
        });
        //console.error(err);
      }
    };
    const userComarcaId = authModule.user.comarcaId;
    watch(error, () => {
      if (!error.value) return;

      Swal.fire({
        text:
          t("errors." + (error.value.error.code ?? error.value.error.name)) ??
          error.value.error.name,
        icon: "error",
        buttonsStyling: false,
        confirmButtonText: t("dialogs.okButton"),
        customClass: {
          confirmButton: "btn btn-primary",
        },
      });
    });

    const cargaDetalle = ref<boolean>(false);
    const guardaDetalle = ref<boolean>(false);
    const estado = ref<string>("");
    const claseEstado = ref<string>("");

    onBeforeMount(async () => {
      cargaDetalle.value = true;
      try {
        await module.fetchById({ comarcaId: comarcaId, id: token as string });
      } catch (err) {
        Swal.fire({
          text: t("formularios.familiaDetalle.cargar.error"),
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: t("dialogs.okButton"),
          customClass: {
            confirmButton: "btn btn-primary",
          },
        });
      } finally {
        cargaDetalle.value = false;
      }
    });

    const datosFamilia = computed(() => module.familia);

    const menoresFamilia = computed(() => {
      return datosFamilia.value?.menores ?? [];
    });

    const menoresServicios = computed(() => {
      return (
        datosFamilia.value?.servicios?.filter(
          (servicio) => servicio.servicioProgramadoId === null
        ) ?? []
      );
    });

    const menoresServiciosColectivos = computed(() => {
      return (
        datosFamilia.value?.servicios?.filter(
          (servicio) => servicio.servicioProgramadoId
        ) ?? []
      );
    });

    const serviciosAprobados = computed(() => {
      return (
        datosFamilia.value?.servicios?.filter(
          (servicio) => servicio.estado === ServicioEstado.aprobado
        ) ?? []
      );
    });

    watch(datosFamilia, () => {
      if (!datosFamilia.value) return;

      comarca.value = datosFamilia.value?.comarca?.nombre ?? "";

      estado.value = datosFamilia.value?.estado.replace("_", " ");
      switch (estado.value.replace(" ", "_")) {
        case "PENDIENTE_DOCUMENTACION":
          claseEstado.value = "badge badge-warning";
          break;
        case "APTO":
          claseEstado.value = "badge badge-success";
          break;
        case "SUSPENDIDO":
          claseEstado.value = "badge badge-danger";
          break;
        default:
          claseEstado.value = "d-none";
          break;
      }

      resetForm({
        values: {
          tratamiento: datosFamilia.value.tratamiento,
          nombre: datosFamilia.value.nombre,
          apellidos: datosFamilia.value.apellidos,
          dni: datosFamilia.value.dni,
          email: datosFamilia.value.email,
          telefono: datosFamilia.value.telefono,
          direccion: datosFamilia.value.direccion,
          provincia: datosFamilia.value.provincia,
          localidad: datosFamilia.value.localidad,
          comarcaId: datosFamilia.value.comarcaId,
          tipologiaFamilia: datosFamilia.value.tipologiaFamilia,
          numeroMenores: datosFamilia.value.numeroMenores,
          conIngresos: datosFamilia.value.conIngresos,
          ingresos: datosFamilia.value.ingresos ?? "",
          familiaPrioritaria: datosFamilia.value.familiaPrioritaria,
          documentosEliminar: datosFamilia.value.documentosEliminar || [],
          documentosNuevos: datosFamilia.value.documentosNuevos || [],
          documentosModificados: datosFamilia.value.documentosModificados || [],
        },
      });
      documentos.value = datosFamilia.value.documentos || [];

      setCurrentPageBreadcrumbs(datosFamilia.value?.dni ?? "", [
        t("menu.personas"),
        { title: t("menu.familias"), path: "/listados/familias" },
      ]);
    });

    const schema = Yup.object({
      tratamiento: Yup.string()
        .required()
        .label(t("formularios.familiaDetalle.tratamiento.label")),
      nombre: Yup.string()
        .required()
        .label(t("formularios.familiaDetalle.nombre.label")),
      apellidos: Yup.string()
        .required()
        .label(t("formularios.familiaDetalle.apellidos.label")),
      dni: Yup.string()
        .required()
        .label(t("formularios.familiaDetalle.dni.label")),
      email: Yup.string()
        .required()
        .email()
        .matches(
          /^(")?(?:[^."])(?:(?:[.])?(?:[\w\-!#$%&'*+/=?^_`{|}~]))*\1@(\w[-\w]*\.){1,5}([A-Za-z]){2,9}$/,
          t("formularios.familiaDetalle.emailValido.label")
        )
        .label(t("formularios.familiaDetalle.email.label")),
      telefono: Yup.string()
        .required()
        .label(t("formularios.familiaDetalle.telefono.label")),
      direccion: Yup.string()
        .required()
        .label(t("formularios.familiaDetalle.direccion.label")),
      provincia: Yup.string()
        .required()
        .label(t("formularios.familiaDetalle.provincia.label")),
      localidad: Yup.string()
        .required()
        .label(t("formularios.familiaDetalle.localidad.label")),
      comarcaId: Yup.string()
        .required()
        .label(t("formularios.familiaDetalle.comarca.label")),
      tipologiaFamilia: Yup.string().required(),
      numeroMenores: Yup.string().required(),
      conIngresos: Yup.boolean().required(),
      ingresos: Yup.string().ensure(),
      familiaPrioritaria: Yup.boolean().required(),
      documentos: Yup.array(),
      documentosEliminar: Yup.array(),
      documentosNuevos: Yup.array(),
      documentosModificados: Yup.array(),
    });

    const { resetForm, handleSubmit } = useForm<Familia>({
      validationSchema: schema,
    });

    const { value: tratamiento } = useField("tratamiento");
    const { value: nombre } = useField("nombre");
    const { value: apellidos } = useField("apellidos");
    const { value: dni } = useField("dni");
    const { value: email } = useField("email");
    const { value: telefono } = useField("telefono");
    const { value: direccion } = useField("direccion");
    const { value: provincia } = useField("provincia");
    const { value: localidad } = useField("localidad");
    const { value: comarca } = useField("comarcaId");
    const { value: tipologiaFamilia } = useField("tipologiaFamilia");
    const { value: numeroMenores } = useField("numeroMenores");
    const { value: conIngresos } = useField("conIngresos");
    const { value: ingresos } = useField("ingresos");
    const { value: familiaPrioritaria } = useField("familiaPrioritaria");

    const documentos = ref<Documento[]>([]);
    const editingIndex = ref<number | null>(null);
    const editingNombre = ref<string>("");
    const { value: documentosEliminar } =
      useField<string[]>("documentosEliminar");
    const { value: documentosNuevos } = useField<IAMFile[]>("documentosNuevos");
    const { value: documentosModificados } = useField<DocumentoModificado[]>(
      "documentosModificados"
    );
    const isReading = ref<boolean>(false);
    let erroresDocumentos = ref<{ [key: string]: boolean }>({});

    return {
      onSendEmail,
      prepareSendEmail,
      sendEmailContext,
      tipologiasFamiliaTodas,
      numeroMenoresOpcionesTodas,
      tratamientos,
      schema,
      cargaDetalle,
      guardaDetalle,
      familia,
      tratamiento,
      nombre,
      apellidos,
      dni,
      email,
      telefono,
      direccion,
      provincia,
      localidad,
      comarcaId: comarca,
      isAdmin: !userComarcaId,
      comarcas,
      familiaPrioritaria,
      tipologiaFamilia,
      numeroMenores,
      conIngresos,
      ingresos,
      comarca,
      menoresServicios,
      menoresServiciosColectivos,
      serviciosAprobados,
      documentos,
      editingIndex,
      editingNombre,
      documentosEliminar,
      documentosNuevos,
      documentosModificados,
      claseEstado,
      estado,
      menoresFamilia,
      isReading,
      erroresDocumentos,
      eliminar: () => {
        Swal.fire({
          text: t("dialogs.eliminar.confirmacion"),
          icon: "question",
          showCancelButton: true,
          confirmButtonText: t("dialogs.okButton"),
          cancelButtonText: t("dialogs.cancelButton"),
          reverseButtons: true,
        }).then(({ isConfirmed }) => {
          if (!isConfirmed) return;

          cargaDetalle.value = true;
          return module
            .eliminar({
              comarcaId: (comarcaId ?? datosFamilia.value?.comarcaId) as string,
              elementId: token as string,
            })
            .then(() => {
              router.replace("/listados/familias");
              ElMessage({
                message: t("dialogs.eliminar.exito"),
                type: "success",
              });
            })
            .catch(() => {
              ElNotification({
                message: t("dialogs.eliminar.error"),
                type: "error",
              });
              //console.error(err);
            })
            .finally(() => {
              cargaDetalle.value = false;
            });
        });
      },
      onSubmit: handleSubmit(async (values) => {
        guardaDetalle.value = true;
        values.ingresos = values.ingresos.toString();
        try {
          await module
            .update({
              comarcaId: comarcaId,
              id: token as string,
              data: values,
            })
            .then(() => {
              if (!module.errors)
                ElNotification({
                  message: t("listados.familiaDetalle.guardar.mensaje.exito"),
                  type: "success",
                });
              //router.replace("/listados/familias");
            });
        } catch (err) {
          ElNotification({
            message: t("formularios.profesionalDetalle.guarda.error"),
            type: "error",
          });
        } finally {
          guardaDetalle.value = false;
        }
      }),
    };
  },

  mounted() {
    const { t } = useI18n();
    const dropzoneNuevosDocumentos = new Dropzone(
      "#kt_dropzonejs_nuevos_documentosFamilia",
      {
        url: process.env.VUE_APP_API_URL,
        // The name that will be used to transfer the file
        paramName: "documento",
        maxFilesize: 10, // MB
        addRemoveLinks: true,
        uploadMultiple: true,
        autoProcessQueue: false,
        acceptedFiles: "image/*,application/pdf",
      }
    );
    dropzoneNuevosDocumentos.on("addedfile", async (file) => {
      this.isReading = true;
      try {
        const fileToAdd = file.type.includes("image/")
          ? await this.compressImage(file)
          : file;

        await this.includeDocumentoNuevo(fileToAdd);
        ElNotification({
          message: t("formularios.profesionalDetalle.guarda.terminado"),
          type: "info",
        });
      } catch (err) {
        Swal.fire({
          text: t("formularios.profesionalDetalle.guarda.errorArchivo"),
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: t("dialogs.okButton"),
          customClass: {
            confirmButton: "btn btn-primary",
          },
        });
        this.erroresDocumentos[file.name] = true;
        this.isReading = true;
      } finally {
        if (Object.keys(this.erroresDocumentos).length === 0) {
          this.isReading = false;
        } else {
          this.isReading = true;
        }
      }
    });
    dropzoneNuevosDocumentos.on("removedfile", (file) => {
      this.deleteDocumentoNuevo(file);
      delete this.erroresDocumentos[file.name];

      if (Object.keys(this.erroresDocumentos).length === 0) {
        this.isReading = false;
      }
    });
  },
});
